import React, { memo, useEffect, useMemo, useRef } from 'react';
// @ts-ignore
import { Draggable, DraggableProvided, DraggableStateSnapshot } from '@nurba94/react-beautiful-dnd-custom-nurba';
import { Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DrawIcon from '@mui/icons-material/DrawOutlined';
import cx from 'classnames';

import { FavouriteStar } from '../../ClipFavourites/Star';
import { AngleLock } from '../../ClipFavourites/AngleLock';
import { useClipMenu } from '../../ClipFavourites/useClipMenu';
import { useClipFilter } from '../../ClipFilter/context';
import { useDrawingMarkers } from '../../VideoDrawing/context';
import { timeLineFormatTime } from '../../../helpers';
import { updateQualifiers, updateNotes } from '../../../store/videos/videoSlice';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { TClip } from '../../Timeline/TimelineClip';
import {
  updateCurrentSelectedComposerClips,
  updateComposerVideoCLips,
  triggerRefresh,
} from '../../../store/composers/composersSlice';
import styles from './ComposerClip.module.scss';
import { CLIP } from '../../../enums';
import { TClipDrawer } from '..';

type TComposerClipProps = {
  clip: TClip;
  markMargin: number;
  rowIndex: number;
  clipIndex: number;
  topicId: string;
  rowTitle: string;
  handleEditClip: ({ id, time_end, time_start, color, topicId }: CLIP) => void;
  clipDrawer: TClipDrawer;
  canDrag: boolean;
  handleSecondaryClick: (e: React.MouseEvent<HTMLDivElement>, clipIndex: number) => void;
  selectClip: (clip: TClip, clipIndex: number, rowIndex: number, doubleClick?: boolean) => void;
  isCollaborator: boolean;
  clipIsSelected: boolean;
  draggableIndex: number;
  switchSendClipModal: () => void;
  hasGeneratedCopy?: boolean;
  showGeneratedDownloadHints?: boolean;
  dimmed: boolean;
  collapsed: boolean;
};

const DrawingClipMarkers = ({ clip, clipWidth }: { clip: TClip; clipWidth: number }) => {
  const { markers, showMarker } = useDrawingMarkers();
  if (clip.time_end <= clip.time_start) return null;
  return <>{markers.filter((comment) => String(comment.clip_id) === String(clip.id) &&
    comment.timestamp >= clip.time_start && comment.timestamp <= clip.time_end).map((comment) => (
    <button key={comment.id} type="button" className={styles.drawingMarker}
      aria-label={`Drawing at ${timeLineFormatTime(comment.timestamp)}`} title={`Drawing · ${timeLineFormatTime(comment.timestamp)}`}
      data-preserve-clip-workspace data-preserve-clip-edit
      style={{ left: Math.max(0, Math.min(clipWidth - 14, clipWidth * (comment.timestamp - clip.time_start) / (clip.time_end - clip.time_start))) }}
      onPointerDown={(event) => event.stopPropagation()} onMouseDown={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
      onClick={(event) => { event.stopPropagation(); showMarker(comment); }}><DrawIcon /></button>
  ))}</>;
};

const ActiveComposerClip: React.FC<TComposerClipProps> = ({
  clip,
  markMargin,
  rowIndex,
  clipIndex,
  clipDrawer,
  topicId,
  canDrag,
  rowTitle: topicName,
  handleSecondaryClick,
  clipIsSelected,
  draggableIndex,
  hasGeneratedCopy,
  showGeneratedDownloadHints,
  dimmed,
  collapsed,
}) => {
  const dispatch = useAppDispatch();
  const { active: filterActive } = useClipFilter();
  const { composerPlayControl, composers, currentSelectedComposerClips, videoLength } = useAppSelector(
    (state) => state.composersStore,
  );

  const clipLength = useMemo(() => {
    const length = clip.time_end - clip.time_start;
    const result = length * markMargin;
    return result;
  }, [markMargin, composers]);

  const ref = useRef<HTMLDivElement>(null);

  const { onContextMenu, menu } = useClipMenu(
    { ...clip, topicId, clipIndex, rowIndex, topicName },
    (event) => handleSecondaryClick(event, clipIndex),
    !!clipDrawer && Object.keys(clipDrawer).length > 0,
  );

  // hover/drop handling lives in ComposerRow (clipsContainer drop zone)

  useEffect(() => {
    if (composerPlayControl.currentClipIndex === clipIndex && composerPlayControl.currentRowIndex === rowIndex) {
      if (clip?.videos?.length) {
        dispatch(updateComposerVideoCLips(clip.videos));
        dispatch(triggerRefresh());
      } else {
        dispatch(updateComposerVideoCLips(null));
        dispatch(triggerRefresh());
      }
      dispatch(updateQualifiers(clip.qualifiers));

      if (topicName || clip.notes) {
        const noteMessages = clip.notes.map((n) => n.note);
        dispatch(updateNotes([noteMessages.map(() => topicName), noteMessages]));
      }

      if (!filterActive && !currentSelectedComposerClips.length) {
        dispatch(updateCurrentSelectedComposerClips([{ ...clip, topicId, clipIndex, rowIndex }]));
      }
    }
  }, [composerPlayControl.currentClipIndex, composerPlayControl.currentRowIndex, currentSelectedComposerClips.length, filterActive]);

  const clipColor = useMemo(() => (clip?.color ? `rgb(${clip.color.split(',').map((c) => +c * 256)})` : ''), []);

  const renderQualifiers = useMemo(() => {
    if (!clip.qualifiers) return null;

    const inRange = clip.qualifiers.filter((q) => q.time >= clip.time_start && q.time <= clip.time_end);

    return inRange.map((qualifier, index) => {
      const ratio = (qualifier.time - clip.time_start) / (clip.time_end - clip.time_start);
      const leftPosition = Math.min(Math.max(0, clipLength * ratio), clipLength);
      const qualifierColor = qualifier.color ? `rgb(${qualifier.color.split(',').map((c) => +c * 256)})` : '';

      return (
        <div
          key={index}
          style={{ backgroundColor: qualifierColor, left: `${leftPosition}px`, color: qualifierColor }}
          className={styles.qualifier}
          id={`${clipIndex}:${rowIndex}`}
        >
          <div className={styles.qualifierTooltip}>{qualifier.name}</div>
        </div>
      );
    });
  }, [markMargin, clip.qualifiers, clip.time_start, clip.time_end, clipLength, clipIndex, rowIndex]);

  const isClipInRange = useMemo(() => {
    return clip.time_start >= 0 && clip.time_start < videoLength && clip.time_end <= videoLength;
  }, [videoLength]);
  const showGeneratedHoverState = !!hasGeneratedCopy && !!showGeneratedDownloadHints;

  return (
    <Draggable draggableId={clip.id} index={draggableIndex} isDragDisabled={!canDrag}>
      {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
        <div ref={provided.innerRef} {...provided.dragHandleProps} {...provided.draggableProps}>
          <Tooltip title={isClipInRange ? '' : 'Clip is out of video range'}>
            <div className={cx(styles.composerContainer, !clipIsSelected && styles.faded, dimmed && styles.dimmed, collapsed && styles.collapsed)}>
              <div className={styles.container} onContextMenu={onContextMenu}>
                <div
                  className={cx(
                    clipIsSelected && styles.selectedClip,
                    showGeneratedHoverState && styles.generatedReadyClip,
                    clipDrawer?.id === clip.id && styles.editedClip,
                  )}
                >
                  <div
                    ref={ref}
                    style={{ width: `${clipLength}px`, background: clipColor }}
                    className={cx(styles.clip, clipIsSelected && styles.playingClip)}
                    id={`${clipIndex}:${rowIndex}`}
                  >
                    <div
                      id={`${clipIndex}:${rowIndex}`}
                      className={clipIsSelected ? styles.secondsActiveContainer : styles.secondsContainer}
                    >
                      {markMargin >= 0.4 && renderQualifiers}
                    </div>
                    <FavouriteStar clipId={clip.id} badge clipWidth={clipLength} angleLocked={clip.locked_angle_id != null} />
                    <AngleLock clip={clip} clipWidth={clipLength} />
                    <DrawingClipMarkers clip={clip} clipWidth={clipLength} />
                    {showGeneratedHoverState && <CheckCircleIcon style={{ top: '0px' }} className={styles.downloadReadyIcon} />}
                  </div>
                </div>
                {snapshot.isDragging && currentSelectedComposerClips.length > 1 && (
                  <div className={styles.draggingClipCount}>
                    +{currentSelectedComposerClips.length}
                    {currentSelectedComposerClips.map((c, i) => (
                      <div
                        key={`${c.id}_${i}`}
                        className={styles.draggingClips}
                        style={{ width: `${clipLength}px`, left: `${10 + i + 2}px`, bottom: `${-15 - i - 2}px` }}
                      />
                    ))}
                  </div>
                )}
                {menu}
              </div>
            </div>
          </Tooltip>
        </div>
      )}
    </Draggable>
  );
};

const DumbComposerClip: React.FC<TComposerClipProps> = ({
  clip,
  topicId,
  rowTitle: topicName,
  clipDrawer,
  handleSecondaryClick,
  clipIndex,
  rowIndex,
  markMargin,
  canDrag,
  draggableIndex,
  hasGeneratedCopy,
  showGeneratedDownloadHints,
  dimmed,
  collapsed,
}) => {
  const { onContextMenu, menu } = useClipMenu(
    { ...clip, topicId, clipIndex, rowIndex, topicName },
    (event) => handleSecondaryClick(event, clipIndex),
    !!clipDrawer && Object.keys(clipDrawer).length > 0,
  );

  const clipLength = useMemo(() => (clip.time_end - clip.time_start) * markMargin, [clip.time_end, clip.time_start, markMargin]);
  const clipColor = useMemo(
    () =>
      clip.color
        ? `rgb(${clip.color
            .split(',')
            .map((c) => +c * 256)
            .join(',')})`
        : '',
    [clip.color],
  );

  const containerStyle = useMemo(() => ({ width: `${clipLength}px`, background: clipColor }), [clipLength, clipColor]);
  const showGeneratedHoverState = !!hasGeneratedCopy && !!showGeneratedDownloadHints;

  // marker drag/drop handling lives in ComposerRow (clipsContainer drop zone)

  return (
    <Draggable draggableId={clip.id} index={draggableIndex} isDragDisabled={!canDrag}>
      {(provided: any) => (
        <div ref={provided.innerRef} {...provided.dragHandleProps} {...provided.draggableProps} onContextMenu={onContextMenu}>
          <div
            id={`${clipIndex}:${rowIndex}`}
            className={cx(styles.DumbClip, showGeneratedHoverState && styles.generatedReadyDumbClip, dimmed && styles.dimmed, collapsed && styles.collapsed)}
            style={containerStyle}
          >
            {markMargin >= 0.4 && (
              <Qualifiers qualifiers={clip.qualifiers || []} clipLength={clipLength} timeStart={clip.time_start} timeEnd={clip.time_end} />
            )}
            <FavouriteStar clipId={clip.id} badge clipWidth={clipLength} angleLocked={clip.locked_angle_id != null} />
            <AngleLock clip={clip} clipWidth={clipLength} />
            <DrawingClipMarkers clip={clip} clipWidth={clipLength} />
            {menu}
            {showGeneratedHoverState && <CheckCircleIcon className={styles.downloadReadyIcon} />}
          </div>
        </div>
      )}
    </Draggable>
  );
};

const ComposerClip: React.FC<any> = (props) => (props.clipIsSelected ? <ActiveComposerClip {...props} /> : <DumbComposerClip {...props} />);

const Qualifiers = React.memo(({ qualifiers = [], timeStart, timeEnd, clipLength }: any) => {
  const markers = useMemo(() => {
    const duration = timeEnd - timeStart;
    const inRange = (qualifiers || []).filter((q: any) => q.time >= timeStart && q.time <= timeEnd);

    return inRange.map((q: any, i: number) => {
      const ratio = (q.time - timeStart) / duration;
      const left = Math.min(Math.max(0, clipLength * ratio), clipLength);
      const qc = q.color
        ? `rgb(${q.color
            .split(',')
            .map((c: string) => +c * 256)
            .join(',')})`
        : '';

      return (
        <div key={i} className={styles.dumbQualifier} style={{ left: `${left}px`, backgroundColor: qc, color: qc }}>
          <div className={styles.dumbQualifierTooltip}>{q.name}</div>
        </div>
      );
    });
  }, [qualifiers, timeStart, timeEnd, clipLength]);

  return <>{markers}</>;
});

export default memo(ComposerClip);
